// 列定義の正規化と、品番入力制御(手入力ロック等)の適用をまとめる。
// 本文は HinbanMaster.js から移設したもの。

export function createFieldNormalizeService(deps) {
    const {
        BRAND_HINBAN_COLUMNID,
        CELLTYPE,
        DicVal,
        FIXED_HINBAN_NUMBERING_COLUMNID,
        FIXED_HINBAN_NUMBERING_LABEL,
        G_id,
        HINBAN_INPUT_CONTROL_CONDITIONAL_COLUMNS,
        HINBAN_INPUT_CONTROL_LOCK_COLUMNS,
        HINBAN_INPUT_CONTROL_NMM_LOCK_COLUMNS,
        HINBAN_INPUT_CONTROL_STATUS,
        HINBAN_MANUAL_INPUT_LOCK_COLUMNS,
        PDT_SYNFLG1_2_DISPLAY_VALUE_MAP,
        PDT_SYNFLG1_DISPLAY_VALUE_MAP,
        PDT_SYNONOFFFLG_DISPLAY_VALUE_MAP,
        entrydataRef,
        firstNonEmptyValue,
        formstatus,
        initialHinbanInputControlDataRef,
        isBrandHinbanNumberingControlledField,
        isEmpty,
        storeentrydata
    } = deps;

    function isFixedHinbanNumberingTarget(field) {
        return String(DicVal(field, "logpid", "")) === G_id.BASE && String(DicVal(field, "type", "")) === "100";
    }
    function normalizePdtSynFlg1Field(field) {
        if (isEmpty(field) || field.columnid !== "PDT_SYNFLG1") return field;
        return {
            ...field,
            displayValueMap: PDT_SYNFLG1_DISPLAY_VALUE_MAP
        };
    }
    function normalizePdtSynFlg1_2Field(field) {
        if (isEmpty(field) || field.columnid !== "PDT_SYNFLG1_2") return field;
        return {
            ...field,
            displayValueMap: PDT_SYNFLG1_2_DISPLAY_VALUE_MAP,
            displayValueSourceColumn: "PDT_SYNFLG1",
            displayDefaultValue: "未申請"
        };
    }
    function normalizePdtSynOnOffFlgField(field) {
        if (isEmpty(field) || field.columnid !== "PDT_SYNONOFFFLG") return field;
        return {
            ...field,
            displayValueMap: PDT_SYNONOFFFLG_DISPLAY_VALUE_MAP
        };
    }
    function normalizePdtTenkaiCodeOptions(options) {
        if (isEmpty(options)) return [];
        const normalized = [];
        const used = new Set();
        options.forEach((option) => {
            const code = String(DicVal(option, "code", "")).trim();
            if (isEmpty(code) || used.has(code)) return;
            used.add(code);
            normalized.push({
                code: code,
                name: String(DicVal(option, "name", "")).trim()
            });
        });
        return normalized;
    }
    function normalizeDisplayField(field) {
        return normalizePdtSynOnOffFlgField(
            normalizePdtSynFlg1_2Field(
                normalizePdtSynFlg1Field(field)
            )
        );
    }
    function removePdtItmcItemCdTriggerOnEntryForm(field) {
        if (formstatus !== "ENTRY FORM" || isEmpty(field) || !Array.isArray(DicVal(field, "trigger", null))) return field;
        const filteredTrigger = field.trigger.filter((trigger) => !matchesColumnId(DicVal(trigger, "columnid", ""), "PDT_ITMCITEMCD"));
        if (filteredTrigger.length === field.trigger.length) return field;
        return { ...field, trigger: filteredTrigger };
    }
    function normalizeField(field) {
        return removePdtItmcItemCdTriggerOnEntryForm(normalizeDisplayField(field));
    }
    function normalizeDisplayFields(fields) {
        if (isEmpty(fields)) return [];
        return fields.map(normalizeField);
    }
    function resolveHinbanInputControlData() {
        return !isEmpty(storeentrydata) ? storeentrydata : entrydataRef.current;
    }
    function isTruthyHinbanControlValue(value) {
        const text = String(isEmpty(value) ? "" : value).trim().toLowerCase();
        return text === "1" || text === "true" || text === "yes";
    }
    function resolveHinbanInputControlStatus(data = resolveHinbanInputControlData()) {
        if (formstatus !== "ENTRY FORM") return "";
        const source = isEmpty(data) ? {} : data;
        if (isTruthyHinbanControlValue(DicVal(source, "PDT_MD_TNYM_EXISTS", "0"))) return HINBAN_INPUT_CONTROL_STATUS.MD_TONYU;
        if (String(DicVal(source, "PDT_SYNFLG1", "")).trim() === "2") return HINBAN_INPUT_CONTROL_STATUS.APPROVED;
        const reqFlg = String(DicVal(source, "PDT_REQFLG", "")).trim();
        if (reqFlg !== "" && reqFlg !== "0" && reqFlg !== "未申請") return HINBAN_INPUT_CONTROL_STATUS.REQUESTED;
        return HINBAN_INPUT_CONTROL_STATUS.REGISTERED;
    }
    function shouldLockHinbanConditionalField(field, columnid, status, data) {
        const isBrandHinbanField = isBrandHinbanNumberingControlledField(field) || matchesColumnId(columnid, BRAND_HINBAN_COLUMNID);
        if (!isBrandHinbanField && !HINBAN_INPUT_CONTROL_CONDITIONAL_COLUMNS.some((target) => matchesColumnId(columnid, target))) return false;
        if (status !== HINBAN_INPUT_CONTROL_STATUS.REGISTERED) return false;
        if (isBrandHinbanField) {
            return String(getEntryDataValue(data, BRAND_HINBAN_COLUMNID, "")).trim() !== "";
        }
        if (matchesColumnId(columnid, "PDT_SHOHINKBN")) {
            return String(getEntryDataValue(initialHinbanInputControlDataRef.current, "PDT_SHOHINKBN", "")).trim() !== "";
        }
        // Add extra rules for triangle-marked fields here.
        return false;
    }
    function shouldLockHinbanInputControlField(field, columnid, status, data) {
        if (isEmpty(columnid) || isEmpty(status)) return false;
        const source = isEmpty(data) ? {} : data;
        if (isTruthyHinbanControlValue(DicVal(source, "PDT_W_NMM_EXISTS", "0"))
            && HINBAN_INPUT_CONTROL_NMM_LOCK_COLUMNS.some((target) => matchesColumnId(columnid, target))) return true;
        if (shouldLockHinbanConditionalField(field, columnid, status, data)) return true;
        const lockColumns = DicVal(HINBAN_INPUT_CONTROL_LOCK_COLUMNS, status, []);
        return lockColumns.some((target) => matchesColumnId(columnid, target));
    }
    function applyHinbanManualInputLockToField(field) {
        if (isEmpty(field)) return field;
        const columnid = DicVal(field, "columnid", DicVal(field, "field", DicVal(field, "colId", "")));
        if (!HINBAN_MANUAL_INPUT_LOCK_COLUMNS.some((target) => matchesColumnId(columnid, target))) return field;
        if (!!DicVal(field, "manualInputLocked", false)) return field;
        return { ...field, manualInputLocked: true };
    }
    function applyHinbanInputControlToFields(fields, data = resolveHinbanInputControlData()) {
        if (isEmpty(fields)) return fields;
        const status = resolveHinbanInputControlStatus(data);
        let changed = false;
        const next = fields.map((field) => {
            if (isEmpty(field)) return field;
            const manualLockedField = applyHinbanManualInputLockToField(field);
            if (manualLockedField !== field) changed = true;
            if (isEmpty(status)) return manualLockedField;
            const columnid = DicVal(manualLockedField, "columnid", DicVal(manualLockedField, "field", DicVal(manualLockedField, "colId", "")));
            if (!shouldLockHinbanInputControlField(manualLockedField, columnid, status, data)) return manualLockedField;
            if (!!DicVal(manualLockedField, "locked", false)) return manualLockedField;
            changed = true;
            return { ...manualLockedField, locked: true };
        });
        return changed ? next : fields;
    }
    function createFixedHinbanNumberingField() {
        return {
            columnid: FIXED_HINBAN_NUMBERING_COLUMNID,
            columnnm: FIXED_HINBAN_NUMBERING_LABEL,
            table: "",
            logpid: G_id.BASE,
            type: "100",
            datatype: CELLTYPE.SPIN,
            options: [{ code: "0", name: "自動" }, { code: "1", name: "手動" }],
            width: 0,
            hidden: "2",
            length: 0,
            format: "",
            decimalplace: 0,
            dbcolumn: "",
            dataid: "",
            initvalue: "0",
            trigger: [],
            required: false,
            locked: true,
            unchange: true,
            primary: false,
            multiple: false,
            history: false,
            __clientOnly: true
        };
    }
    function normalizeNumberText(value) {
        if (isEmpty(value)) return "";
        return String(value).replace(/,/g, "").trim();
    }
    function matchesColumnId(columnid, target) {
        return String(isEmpty(columnid) ? "" : columnid).trim().toUpperCase() === String(isEmpty(target) ? "" : target).trim().toUpperCase();
    }
    function matchesColumnToken(columnid, target) {
        const left = String(isEmpty(columnid) ? "" : columnid).trim().toUpperCase();
        const right = String(isEmpty(target) ? "" : target).trim().toUpperCase();
        return left === right || left.endsWith(right) || left.includes(right);
    }
    function resolveEntryDataKey(targetData, columnid) {
        if (isEmpty(targetData) || isEmpty(columnid)) return columnid;
        const keys = Object.keys(targetData);
        const exact = keys.find((key) => matchesColumnId(key, columnid));
        if (!isEmpty(exact)) return exact;
        const alias = keys.find((key) => matchesColumnToken(key, columnid));
        return isEmpty(alias) ? columnid : alias;
    }
    function getEntryDataValue(targetData, columnid, defaultValue) {
        const key = resolveEntryDataKey(targetData, columnid);
        return DicVal(targetData, key, defaultValue);
    }
    function setEntryDataValue(targetData, columnid, value) {
        if (isEmpty(targetData) || isEmpty(columnid)) return false;
        const key = resolveEntryDataKey(targetData, columnid);
        if (String(DicVal(targetData, key, "")) === String(value)) return false;
        targetData[key] = value;
        return true;
    }
    function applyFixedHinbanNumberingValue(targetData, sourceData) {
        if (isEmpty(targetData)) return false;
        const value = String(firstNonEmptyValue(sourceData, ["BRD_SAIBAN", "brd_saiban"]) || "").trim();
        return setEntryDataValue(targetData, FIXED_HINBAN_NUMBERING_COLUMNID, value === "1" ? "1" : "0");
    }

    return {
        isFixedHinbanNumberingTarget,
        normalizePdtSynFlg1Field,
        normalizePdtSynFlg1_2Field,
        normalizePdtSynOnOffFlgField,
        normalizePdtTenkaiCodeOptions,
        normalizeDisplayField,
        removePdtItmcItemCdTriggerOnEntryForm,
        normalizeField,
        normalizeDisplayFields,
        resolveHinbanInputControlData,
        isTruthyHinbanControlValue,
        resolveHinbanInputControlStatus,
        shouldLockHinbanConditionalField,
        shouldLockHinbanInputControlField,
        applyHinbanManualInputLockToField,
        applyHinbanInputControlToFields,
        createFixedHinbanNumberingField,
        normalizeNumberText,
        matchesColumnId,
        matchesColumnToken,
        resolveEntryDataKey,
        getEntryDataValue,
        setEntryDataValue,
        applyFixedHinbanNumberingValue
    };
}
